import { Mail, MoreHorizontal, RefreshCw, UserX } from 'lucide-react';

import { canTake, type UserRow } from '../userViewModel';

import UserRowActionButton from './UserRowActionButton';

interface UserRowActionsProps {
  user: UserRow;
  onSelect: (user: UserRow) => void;
  /** Opens the invite drawer pre-linked to this row. */
  onInvite?: (user: UserRow) => void;
  /** Re-sends the invite email. */
  onResendInvite?: (user: UserRow) => void;
  /** Opens the revoke confirmation. */
  onRevokeInvite?: (user: UserRow) => void;
}

/** Trailing-cell actions for a row in the users table: only the ones the API
 *  lists for that row. */
export default function UserRowActions({
  user,
  onSelect,
  onInvite,
  onResendInvite,
  onRevokeInvite,
}: UserRowActionsProps) {
  return (
    <td className="py-2.5 pr-[18px] pl-2 border-b border-stone-200 dark:border-stone-800 text-right whitespace-nowrap">
      {canTake(user, 'invite') && (
        <UserRowActionButton
          icon={Mail}
          label="Send invite"
          title="Open the invite drawer pre-linked to this imported record"
          onClick={() => onInvite?.(user)}
        />
      )}
      {canTake(user, 'resend-invite') && (
        <UserRowActionButton
          icon={RefreshCw}
          label="Resend invite"
          title="Send the invitation email again"
          onClick={() => onResendInvite?.(user)}
        />
      )}
      {canTake(user, 'revoke-invite') && (
        <UserRowActionButton
          icon={UserX}
          label="Revoke invite"
          title="Cancel the invitation and delete the pending sign-in account"
          tone="danger"
          onClick={() => onRevokeInvite?.(user)}
        />
      )}
      <button
        type="button"
        className="w-[26px] h-[26px] rounded-md text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-[#242d28] inline-flex items-center justify-center"
        aria-label="Open user details"
        onClick={(e) => {
          e.stopPropagation();
          onSelect(user);
        }}
      >
        <MoreHorizontal size={16} />
      </button>
    </td>
  );
}
